import { usePathname, useRouter } from "next/navigation";
import { useEffect, useRef } from "react";

/**
 * Hook to handle scroll restoration for navigation
 * - Always starts at top when navigating to a new page
 * - Handles hash navigation properly
 * - Works with Next.js App Router
 */
export const useScrollRestoration = () => {
  const router = useRouter();
  const pathname = usePathname();
  const previousPathnameRef = useRef<string | null>(null);

  useEffect(() => {
    // Check if this is a new page navigation (not just a hash change)
    const isNewPage =
      previousPathnameRef.current !== null &&
      previousPathnameRef.current !== pathname;

    // Update the previous pathname
    previousPathnameRef.current = pathname;

    // Scroll to top on new page navigation (unless there's a hash)
    const scrollToTop = () => {
      const hasHash = window.location.hash;

      // If it's a new page and no hash, scroll to top
      if (isNewPage && !hasHash) {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      }
      // If it's the initial page load and no hash, scroll to top
      else if (!previousPathnameRef.current && !hasHash) {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      }
    };

    // Execute scroll restoration
    scrollToTop();

    // Handle browser navigation (back/forward)
    const handlePopState = () => {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        if (!window.location.hash) {
          window.scrollTo({ top: 0, left: 0, behavior: "instant" });
        }
      }, 50);
    };

    // Handle page unload
    const handleBeforeUnload = () => {
      // Don't interfere if there's a hash navigation
      if (!window.location.hash) {
        window.scrollTo(0, 0);
      }
    };

    window.addEventListener("popstate", handlePopState);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [pathname]);

  // Function to navigate to a new page and ensure scroll top
  const navigateAndScrollTop = (url: string) => {
    router.push(url);
    // Small delay to ensure navigation completes
    setTimeout(() => {
      if (!url.includes("#")) {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      }
    }, 100);
  };

  return {
    navigateAndScrollTop,
  };
};
